import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from './Modal';
import { Field, FormError, inputStyle } from './form';
import { btnGhost, btnPrimary } from './buttons';
import { creatorApi } from '../api/creator';
import type { ApiError } from '../api/client';

const Schema = z.object({
  code: z
    .string()
    .trim()
    .min(3, '3–32 characters')
    .max(32, '3–32 characters')
    .regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, underscore, hyphen only'),
  label: z.string().trim().min(1, 'Add a short label').max(100),
});

type FormValues = z.infer<typeof Schema>;

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Submit a new side code for admin approval. The code lands in `pending`
 * until the team reviews it; the codes table reflects that immediately.
 */
export function RequestSideCodeModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { code: '', label: '' },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => creatorApi.requestSideCode({ code: v.code.toUpperCase(), label: v.label }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator', 'codes'] });
      reset();
      onClose();
    },
    onError: (err: ApiError) => setFormError(err?.message ?? 'Could not submit code'),
  });

  const onSubmit = (vals: FormValues) => {
    const parsed = Schema.safeParse(vals);
    if (!parsed.success) {
      setFormError(parsed.error.errors[0]?.message ?? 'Invalid input');
      return;
    }
    setFormError(null);
    mutation.mutate(parsed.data);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request a side code"
      description="Pick a memorable code for a campaign or platform. Our team reviews it before it goes live."
      width={460}
      footer={
        <>
          <button style={btnGhost} onClick={onClose} type="button">Cancel</button>
          <button style={btnPrimary} type="submit" form="side-code-form" disabled={mutation.isPending}>
            {mutation.isPending ? 'Submitting…' : 'Submit for review'}
          </button>
        </>
      }
    >
      <form id="side-code-form" onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Code" error={errors.code?.message}>
          <input
            {...register('code')}
            autoFocus
            placeholder="SUMMER10"
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
          />
        </Field>
        <Field label="Label" error={errors.label?.message}>
          <input {...register('label')} placeholder="Summer YouTube campaign" style={inputStyle} />
        </Field>
        <FormError message={formError} />
      </form>
    </Modal>
  );
}
